import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useEffect } from "react";
import {
  type NotificationShadowComparisonFilterInput,
  useNotificationShadowComparisonsQuery,
} from "../../generated/graphql";
import { formatShadowTimestamp } from "./notification-shadow-format";
import { shadowStatusChipColor } from "./notification-shadow-status";

const styles = {
  loading: { display: "flex", alignItems: "center", gap: 1, p: 2 },
  empty: { p: 2 },
  sourceRef: { fontFamily: "monospace" },
  mismatchedFields: { flexWrap: "wrap" },
} satisfies Record<string, SxProps<Theme>>;

type NotificationShadowTableProps = {
  readonly filter: NotificationShadowComparisonFilterInput;
  /** Bumped by the page's refresh control; 0 means "no manual refresh yet". */
  readonly refreshKey: number;
};

/** Renders whether a side (legacy or engine) recorded content, without exposing what it recorded. */
const ContentPresenceIcon = ({ present }: { readonly present: boolean }) => (
  <Tooltip title={present ? "Recorded" : "Not recorded"}>
    {present ? (
      <CheckCircleIcon fontSize="small" color="success" />
    ) : (
      <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
    )}
  </Tooltip>
);

/**
 * Table of shadow-comparison rows for the current filter, newest first (the
 * backend already orders by `createdAt desc`). Only PENDING, MISMATCH, and
 * ORPHAN rows are ever returned: a MATCH self-prunes at write time, so an
 * empty result means the shadow run is clean, not that it hasn't run.
 */
const NotificationShadowTable = ({
  filter,
  refreshKey,
}: NotificationShadowTableProps) => {
  const { data, loading, error, refetch } =
    useNotificationShadowComparisonsQuery({ variables: { filter } });

  useEffect(() => {
    if (refreshKey === 0) return;
    void refetch();
  }, [refreshKey, refetch]);

  if (error !== undefined) {
    return (
      <Alert severity="error">
        Failed to load shadow comparisons: {error.message}
      </Alert>
    );
  }

  if (loading && data === undefined) {
    return (
      <Box sx={styles.loading}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading comparisons…
        </Typography>
      </Box>
    );
  }

  const rows = data?.notificationShadowComparisons ?? [];

  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={styles.empty}>
        <Typography color="text.secondary">
          No unresolved/divergent comparisons -- shadow is clean or not yet
          running.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Event type</TableCell>
            <TableCell>Channel</TableCell>
            <TableCell>Source ref</TableCell>
            <TableCell>Mismatched fields</TableCell>
            <TableCell align="center">Legacy content</TableCell>
            <TableCell align="center">Engine content</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.uuid} hover>
              <TableCell>
                <Chip
                  size="small"
                  label={row.status}
                  color={shadowStatusChipColor(row.status)}
                />
              </TableCell>
              <TableCell>{row.eventType}</TableCell>
              <TableCell>{row.channel}</TableCell>
              <TableCell sx={styles.sourceRef}>{row.sourceRef}</TableCell>
              <TableCell>
                {row.mismatchedFields.length === 0 ? (
                  "--"
                ) : (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={styles.mismatchedFields}
                  >
                    {row.mismatchedFields.map((field) => (
                      <Chip
                        key={field}
                        size="small"
                        label={field}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </TableCell>
              <TableCell align="center">
                <ContentPresenceIcon present={row.hasLegacyContent} />
              </TableCell>
              <TableCell align="center">
                <ContentPresenceIcon present={row.hasEngineContent} />
              </TableCell>
              <TableCell>{formatShadowTimestamp(row.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default NotificationShadowTable;
